import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { forkJoin, of } from 'rxjs';
import { DividendoService } from '../../../services/dividendo.service';
import { SociosService } from '../../../services/socios.service';
import { SocioResponse } from '../../../models/socioResponse.model';
import {
  BeneficiarioFormulario,
  DividendoResponse,
  InserirDividendoRequest,
  RetificarDividendoRequest
} from '../../../models/dividendo.model';
import {
  FormaDistribuicaoDividendoEnum,
  FormaPagamentoDividendoEnum,
  ModoDistribuicaoBeneficiarioEnum
} from '../../../models/enums/dividendo.enum';
import { SpinnerService } from '../../../services/spinner.service';
import { ToastService } from '../../../services/toast.service';
import { SeveridadeToast } from '../../../models/enums/severidade-toast.enum';
import { JwtHelper } from '../../../helpers/jwtDecode.helper';
import { retornaDescricao } from '../../../shared/mock/descricao';

@Component({
  selector: 'app-dividendos-adicionar',
  templateUrl: './dividendos-adicionar.component.html',
  styleUrls: ['./dividendos-adicionar.component.scss']
})
export class DividendosAdicionarComponent implements OnInit, OnDestroy {
  @Output() tituloChange = new EventEmitter<string>();
  @Output() iconeChange = new EventEmitter<string>();
  @Output() tituloDescricao = new EventEmitter<string>();
  @Output() textoDescricao = new EventEmitter<string>();
  @Output() mostrarDescricao = new EventEmitter<boolean>();

  usuarioId = '';
  modoRetificacao = false;
  dividendoOrigemId?: string;

  dataDistribuicao?: Date;
  dataPagamento?: Date;
  formaPagamento: FormaPagamentoDividendoEnum = FormaPagamentoDividendoEnum.TransferenciaBancaria;
  formaDistribuicao: FormaDistribuicaoDividendoEnum = FormaDistribuicaoDividendoEnum.Regular;
  modoDistribuicao: ModoDistribuicaoBeneficiarioEnum = ModoDistribuicaoBeneficiarioEnum.Proporcional;
  valorBrutoTotal: number | null = null;
  valorIrTotal = 0;
  valorLiquidoTotal = 0;

  socios: SocioResponse[] = [];
  beneficiarios: BeneficiarioFormulario[] = [];
  mensagemValidacao?: string;
  alertaLucrosAcumulados?: string;

  isCalculando = false;
  isSalvando = false;

  passosFormulario: MenuItem[] = [
    { label: 'Dados da distribuição' },
    { label: 'Beneficiários' },
    { label: 'Revisão e confirmação' }
  ];

  private calculoRequestId = 0;

  formasPagamento = [
    { label: 'Espécie', value: FormaPagamentoDividendoEnum.Especie },
    { label: 'Transferência Bancária', value: FormaPagamentoDividendoEnum.TransferenciaBancaria }
  ];

  formasDistribuicao = [
    { label: 'Distribuição Regular (após apuração do lucro)', value: FormaDistribuicaoDividendoEnum.Regular },
    { label: 'Antecipação de Dividendos (antes da apuração do lucro)', value: FormaDistribuicaoDividendoEnum.Antecipacao }
  ];

  modosDistribuicao = [
    { label: 'Proporcional', value: ModoDistribuicaoBeneficiarioEnum.Proporcional },
    { label: 'Individualizado', value: ModoDistribuicaoBeneficiarioEnum.Individualizado }
  ];

  readonly ModoDistribuicaoBeneficiarioEnum = ModoDistribuicaoBeneficiarioEnum;
  readonly FormaPagamentoDividendoEnum = FormaPagamentoDividendoEnum;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dividendoService: DividendoService,
    private sociosService: SociosService,
    private spinnerService: SpinnerService,
    private toastService: ToastService,
    private jwtHelper: JwtHelper
  ) { }

  ngOnInit(): void {
    this.usuarioId = this.jwtHelper.getUserIdFromToken();
    this.modoRetificacao = this.route.snapshot.url.some(segment => segment.path === 'retificar');
    this.dividendoOrigemId = this.route.snapshot.paramMap.get('id') ?? undefined;

    this.tituloChange.emit(this.modoRetificacao ? 'Retificar Distribuição de Dividendos' : 'Nova Distribuição de Dividendos');
    this.iconeChange.emit('pi pi-plus');
    this.tituloDescricao.emit(retornaDescricao('dividendos')?.titulo ?? 'Distribuição de Dividendos');
    this.textoDescricao.emit(retornaDescricao('dividendos')?.texto ?? 'Cadastre as distribuições de dividendos. O sistema gera automaticamente os lançamentos contábeis conforme a forma de distribuição.');
    this.mostrarDescricao.emit(true);

    this.carregarSocios();
  }

  ngOnDestroy(): void {
    this.mostrarDescricao.emit(false);
  }

  get indicePassoAtivo(): number {
    if (!this.dataDistribuicao || !this.temValorBrutoValido()) {
      return 0;
    }

    if (!this.possuiBeneficiariosValidos()) {
      return 1;
    }

    return 2;
  }

  get formularioProntoParaSalvar(): boolean {
    return !!this.dataDistribuicao
      && this.temValorBrutoValido()
      && this.possuiBeneficiariosValidos()
      && !this.mensagemValidacao
      && !this.isCalculando;
  }

  carregarSocios(): void {
    if (!this.usuarioId) return;

    this.spinnerService.show('Carregando sócios...');
    this.sociosService.listarSocios(this.usuarioId).subscribe({
      next: (socios) => {
        this.socios = socios;
        this.inicializarBeneficiarios();

        if (this.modoRetificacao && this.dividendoOrigemId) {
          this.carregarDividendoParaRetificacao(this.dividendoOrigemId);
        } else {
          this.spinnerService.hide();
        }
      },
      error: () => {
        this.spinnerService.hide();
        this.toastService.setMensagemToast('Erro ao carregar sócios.', SeveridadeToast.Error);
      }
    });
  }

  carregarDividendoParaRetificacao(id: string): void {
    this.dividendoService.obter(id, this.usuarioId).subscribe({
      next: (dividendo) => {
        this.preencherFormulario(dividendo);
        this.executarCalculos(true);
      },
      error: () => {
        this.spinnerService.hide();
        this.toastService.setMensagemToast('Erro ao carregar dividendo para retificação.', SeveridadeToast.Error);
        this.voltar();
      }
    });
  }

  preencherFormulario(dividendo: DividendoResponse): void {
    this.dataDistribuicao = new Date(dividendo.dataDistribuicao);
    this.dataPagamento = dividendo.dataPagamento ? new Date(dividendo.dataPagamento) : undefined;
    this.formaPagamento = dividendo.formaPagamento;
    this.formaDistribuicao = dividendo.formaDistribuicao;
    this.modoDistribuicao = dividendo.modoDistribuicaoBeneficiario;
    this.valorBrutoTotal = dividendo.valorBrutoTotal;
    this.valorIrTotal = dividendo.valorIrTotal;
    this.valorLiquidoTotal = dividendo.valorLiquidoTotal;
    this.beneficiarios = this.socios.map(socio => {
      const existente = dividendo.beneficiarios.find(b => b.socioId === socio.id);
      return {
        socioId: socio.id,
        nomeSocio: socio.nmTrab ?? '',
        participacaoSocietaria: socio.participacaoSocietaria,
        percentual: existente != null ? (existente.percentual ?? 0) : 0,
        valorBruto: existente?.valorBruto ?? 0,
        valorIr: existente?.valorIr ?? 0,
        valorLiquido: existente?.valorLiquido ?? 0
      };
    });
  }

  inicializarBeneficiarios(): void {
    this.beneficiarios = this.socios.map(socio => ({
      socioId: socio.id,
      nomeSocio: socio.nmTrab ?? '',
      participacaoSocietaria: socio.participacaoSocietaria,
      percentual: socio.participacaoSocietaria ?? 0,
      valorBruto: 0,
      valorIr: 0,
      valorLiquido: 0
    }));
  }

  onValorBrutoBlur(): void {
    this.executarCalculos(true, true);
  }

  onBeneficiarioBlur(): void {
    this.executarCalculos(true, false);
  }

  onFormaDistribuicaoChange(): void {
    this.executarCalculos(this.temValorBrutoInformado(), false);
  }

  onModoDistribuicaoChange(): void {
    const redistribuir = this.modoDistribuicao === ModoDistribuicaoBeneficiarioEnum.Individualizado;
    this.executarCalculos(this.temValorBrutoInformado(), redistribuir);
  }

  executarCalculos(mostrarSpinner = true, sincronizarIndividualizadoComTotal = false): void {
    if (!this.temValorBrutoInformado() || this.beneficiarios.length === 0) {
      this.limparTotaisCalculados();
      return;
    }

    if (this.valorBrutoTotal === 0) {
      this.aplicarTotalZerado();
      return;
    }

    if (
      sincronizarIndividualizadoComTotal
      && this.modoDistribuicao === ModoDistribuicaoBeneficiarioEnum.Individualizado
    ) {
      this.sincronizarValoresIndividualizadosComTotal(this.valorBrutoTotal!);
    }

    const beneficiariosRequest = this.montarBeneficiariosParaCalculo();
    if (beneficiariosRequest.length === 0) {
      this.limparTotaisCalculados();
      return;
    }

    const requestId = ++this.calculoRequestId;
    this.isCalculando = true;

    if (mostrarSpinner) {
      this.spinnerService.show('Calculando valores e impostos...');
    }

    const preview$ = this.dividendoService.calcularPreview({
      valorBrutoTotal: this.valorBrutoTotal!,
      modoDistribuicaoBeneficiario: this.modoDistribuicao,
      beneficiarios: beneficiariosRequest
    });

    const alerta$ = this.formaDistribuicao === FormaDistribuicaoDividendoEnum.Regular
      ? this.dividendoService.obterAlertaLucrosAcumulados(this.usuarioId, this.valorBrutoTotal!, this.formaDistribuicao)
      : of({ exibirAlerta: false, mensagem: '', saldoLucrosAcumulados: 0 });

    forkJoin({ preview: preview$, alerta: alerta$ }).subscribe({
      next: ({ preview, alerta }) => {
        if (requestId !== this.calculoRequestId) return;

        this.valorIrTotal = preview.valorIrTotal;
        this.valorLiquidoTotal = preview.valorLiquidoTotal;
        this.mensagemValidacao = preview.somaBeneficiariosValida ? undefined : preview.mensagemValidacao;
        this.alertaLucrosAcumulados = alerta.exibirAlerta ? alerta.mensagem : undefined;

        this.beneficiarios.forEach(beneficiario => {
          const calc = preview.beneficiarios.find(c => c.socioId === beneficiario.socioId);
          if (calc) {
            beneficiario.valorBruto = calc.valorBruto;
            beneficiario.valorIr = calc.valorIr;
            beneficiario.valorLiquido = calc.valorLiquido;
            if (calc.percentual !== undefined && calc.percentual !== null) {
              beneficiario.percentual = calc.percentual;
            }
          } else {
            beneficiario.valorBruto = 0;
            beneficiario.valorIr = 0;
            beneficiario.valorLiquido = 0;
          }
        });

        this.finalizarCalculo();
      },
      error: () => {
        if (requestId !== this.calculoRequestId) return;
        this.toastService.setMensagemToast('Erro ao calcular valores do dividendo.', SeveridadeToast.Error);
        this.finalizarCalculo();
      }
    });
  }

  salvar(): void {
    if (!this.dataDistribuicao) {
      this.toastService.setMensagemToast('Informe a data de distribuição.', SeveridadeToast.Error);
      return;
    }

    if (!this.temValorBrutoValido()) {
      this.toastService.setMensagemToast('Informe o valor bruto total.', SeveridadeToast.Error);
      return;
    }

    if (this.isCalculando) {
      this.toastService.setMensagemToast('Aguarde o término do cálculo dos valores.', SeveridadeToast.Warn);
      return;
    }

    const beneficiariosRequest = this.montarBeneficiariosParaSalvar();
    if (beneficiariosRequest.length === 0) {
      this.toastService.setMensagemToast('Informe ao menos um beneficiário.', SeveridadeToast.Error);
      return;
    }

    if (this.mensagemValidacao) {
      this.toastService.setMensagemToast(this.mensagemValidacao, SeveridadeToast.Error);
      return;
    }

    this.isSalvando = true;
    this.spinnerService.show(this.modoRetificacao ? 'Salvando retificação...' : 'Salvando dividendo...');

    if (this.modoRetificacao && this.dividendoOrigemId) {
      const request: RetificarDividendoRequest = {
        usuarioId: this.usuarioId,
        dataDistribuicao: this.formatarData(this.dataDistribuicao),
        dataPagamento: this.dataPagamento ? this.formatarData(this.dataPagamento) : undefined,
        formaPagamento: this.formaPagamento,
        formaDistribuicao: this.formaDistribuicao,
        modoDistribuicaoBeneficiario: this.modoDistribuicao,
        valorBrutoTotal: this.valorBrutoTotal!,
        beneficiarios: beneficiariosRequest
      };

      this.dividendoService.retificar(this.dividendoOrigemId, request).subscribe({
        next: () => this.finalizarSucesso('Dividendo retificado com sucesso.'),
        error: (error) => this.finalizarErro(error)
      });
      return;
    }

    const request: InserirDividendoRequest = {
      usuarioId: this.usuarioId,
      dataDistribuicao: this.formatarData(this.dataDistribuicao),
      dataPagamento: this.dataPagamento ? this.formatarData(this.dataPagamento) : undefined,
      formaPagamento: this.formaPagamento,
      formaDistribuicao: this.formaDistribuicao,
      modoDistribuicaoBeneficiario: this.modoDistribuicao,
      valorBrutoTotal: this.valorBrutoTotal!,
      beneficiarios: beneficiariosRequest
    };

    this.dividendoService.inserir(request).subscribe({
      next: () => this.finalizarSucesso('Dividendo cadastrado com sucesso.'),
      error: (error) => this.finalizarErro(error)
    });
  }

  voltar(): void {
    this.router.navigate(['/dividendos']);
  }

  get exibirOrientacaoContabilRegular(): boolean {
    return this.formaDistribuicao === FormaDistribuicaoDividendoEnum.Regular;
  }

  get exibirOrientacaoContabilAntecipacao(): boolean {
    return this.formaDistribuicao === FormaDistribuicaoDividendoEnum.Antecipacao;
  }

  get exibirOrientacaoExtrato(): boolean {
    return this.formaPagamento === FormaPagamentoDividendoEnum.TransferenciaBancaria && !!this.dataPagamento;
  }

  get formaDistribuicaoDescricao(): string {
    return this.formasDistribuicao.find(f => f.value === this.formaDistribuicao)?.label ?? '';
  }

  get formaPagamentoDescricao(): string {
    return this.formasPagamento.find(f => f.value === this.formaPagamento)?.label ?? '';
  }

  private montarBeneficiariosParaCalculo() {
    return this.beneficiarios.map(b => ({
      socioId: b.socioId,
      percentual: b.percentual ?? 0,
      valorBruto: b.valorBruto ?? 0
    }));
  }

  private montarBeneficiariosParaSalvar() {
    return this.beneficiarios
      .filter(b => this.modoDistribuicao === ModoDistribuicaoBeneficiarioEnum.Proporcional
        ? (b.percentual ?? 0) > 0
        : (b.valorBruto ?? 0) > 0)
      .map(b => ({
        socioId: b.socioId,
        percentual: b.percentual ?? 0,
        valorBruto: b.valorBruto ?? 0
      }));
  }

  private possuiBeneficiariosValidos(): boolean {
    return this.montarBeneficiariosParaSalvar().length > 0 && !this.mensagemValidacao;
  }

  private aplicarTotalZerado(): void {
    this.calculoRequestId++;
    this.zerarValoresMonetariosBeneficiarios();
    this.valorIrTotal = 0;
    this.valorLiquidoTotal = 0;
    this.alertaLucrosAcumulados = undefined;
    this.mensagemValidacao = this.obterMensagemValidacaoPercentuais();
    this.isCalculando = false;
  }

  private sincronizarValoresIndividualizadosComTotal(valorTotal: number): void {
    const quantidade = this.beneficiarios.length;
    if (quantidade === 0 || valorTotal <= 0) return;

    const somaAtual = this.arredondar2(
      this.beneficiarios.reduce((acc, b) => acc + (b.valorBruto ?? 0), 0)
    );

    if (somaAtual <= 0) {
      const valorBase = this.arredondar2(valorTotal / quantidade);
      let acumulado = 0;

      this.beneficiarios.forEach((beneficiario, index) => {
        if (index === quantidade - 1) {
          beneficiario.valorBruto = this.arredondar2(valorTotal - acumulado);
        } else {
          beneficiario.valorBruto = valorBase;
          acumulado = this.arredondar2(acumulado + valorBase);
        }
      });
      return;
    }

    if (somaAtual === this.arredondar2(valorTotal)) {
      return;
    }

    let acumulado = 0;
    this.beneficiarios.forEach((beneficiario, index) => {
      if (index === quantidade - 1) {
        beneficiario.valorBruto = this.arredondar2(valorTotal - acumulado);
      } else {
        const proporcao = (beneficiario.valorBruto ?? 0) / somaAtual;
        const novoValor = this.arredondar2(valorTotal * proporcao);
        beneficiario.valorBruto = novoValor;
        acumulado = this.arredondar2(acumulado + novoValor);
      }
    });
  }

  private arredondar2(valor: number): number {
    return Math.round((valor + Number.EPSILON) * 100) / 100;
  }

  private zerarValoresMonetariosBeneficiarios(): void {
    this.beneficiarios.forEach(b => {
      b.valorBruto = 0;
      b.valorIr = 0;
      b.valorLiquido = 0;
    });
  }

  private obterMensagemValidacaoPercentuais(): string | undefined {
    if (this.modoDistribuicao !== ModoDistribuicaoBeneficiarioEnum.Proporcional) {
      return undefined;
    }

    const somaPercentuais = this.beneficiarios.reduce((acc, b) => acc + (b.percentual ?? 0), 0);
    return Math.round(somaPercentuais * 10000) / 10000 === 100
      ? undefined
      : 'A soma dos percentuais deve ser igual a 100%.';
  }

  private limparTotaisCalculados(): void {
    this.valorIrTotal = 0;
    this.valorLiquidoTotal = 0;
    this.mensagemValidacao = undefined;
    this.alertaLucrosAcumulados = undefined;
    this.zerarValoresMonetariosBeneficiarios();
  }

  private temValorBrutoInformado(): boolean {
    return this.valorBrutoTotal !== null
      && this.valorBrutoTotal !== undefined
      && !Number.isNaN(this.valorBrutoTotal)
      && this.valorBrutoTotal >= 0;
  }

  private temValorBrutoValido(): boolean {
    return this.temValorBrutoInformado() && this.valorBrutoTotal! > 0;
  }

  private finalizarCalculo(): void {
    this.isCalculando = false;
    this.spinnerService.hide();
  }

  private finalizarSucesso(mensagem: string): void {
    this.isSalvando = false;
    this.spinnerService.hide();
    this.toastService.setMensagemToast(mensagem, SeveridadeToast.Success);
    this.voltar();
  }

  private finalizarErro(error: any): void {
    this.isSalvando = false;
    this.spinnerService.hide();
    this.toastService.setMensagemToast(error.error ?? 'Erro ao salvar dividendo.', SeveridadeToast.Error);
  }

  private formatarData(data: Date): string {
    return data.toISOString().split('T')[0];
  }
}
